/* eslint-disable react/no-children-prop */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton, Link, Stack, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import React from 'react';
import LogoBlueSvg from '../assets/socotra-blue.svg';
import { useAuthContext } from '../context/auth';
import {
	ROLE_SELECT_FIELD_REQUIRED_MESSAGE,
	ROLE_SELECT_NAMESPACE_FIELD_LABEL,
	ROLE_SELECT_SELECT_NAMESPACE_LABEL,
	ROLE_SELECT_SWITCH_ADMINISTRATION_LABEL,
	ROLE_SELECT_SWITCH_AND_LOGOUT_BUTTON_LABEL,
	ROLE_SELECT_SWITCH_LABEL,
	ROLE_SELECT_TENANT_FIELD_LABEL,
	ROLE_SELECT_WELCOME_LABEL,
} from '../data/labelsAndMessages';
import { RoleTree, UserRole } from './admin/namespace/types';
import Error from './Error';

interface Props {
	roleTree: RoleTree;
	activateRole: (role: UserRole) => void;
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<Box
			role='tabpanel'
			sx={{ width: '100%' }}
			hidden={value !== index}
			id={`login-tabpanel-${index}`}
			aria-labelledby={`login-tab-${index}`}
			{...other}
		>
			{children}
		</Box>
	);
}

const RoleSelect = ({ roleTree, activateRole }: Props) => {
	const authData = useAuthContext();
	const [selectedUserRole, setSelectedUserRole] = React.useState<UserRole>();
	const [selectedUserError, setSelectedUserError] =
		React.useState<boolean>(false);
	const [selectedUserTouched, setSelectedUserTouched] =
		React.useState<boolean>(false);
	const [selectedAdminRole, setSelectedAdminRole] = React.useState<UserRole>();
	const [selectedAdminRoleError, setSelectedAdminRoleError] =
		React.useState<boolean>(false);
	const [selectedAdminRoleTouched, setSelectedAdminRoleTouched] =
		React.useState<boolean>(false);
	const [tab, setTab] = React.useState(0);

	const handleSelect = (
		selectedRole: string,
		environment: string,
		isAdmin: boolean,
	) => {
		if (isAdmin) {
			setSelectedAdminRoleError(false);
		} else {
			setSelectedUserError(false);
		}

		const userRole = roleTree[environment].roles.find(
			(role) => role.role === selectedRole,
		);
		isAdmin ? setSelectedAdminRole(userRole) : setSelectedUserRole(userRole);
	};

	const handleSubmit = async (isAdmin: boolean) => {
		if (isAdmin && !selectedAdminRole) {
			setSelectedAdminRoleError(true);
			setSelectedAdminRoleTouched(true);
		} else if (!isAdmin && !selectedUserRole) {
			setSelectedUserError(true);
			setSelectedUserTouched(true);
		} else {
			await activateRole(isAdmin ? selectedAdminRole : selectedUserRole);
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
		setTab(newTab);
	};

	const adminEnvironments =
		roleTree &&
		Object.keys(roleTree).filter((tenant: string) => roleTree[tenant].isAdmin);
	const tenantHostnames =
		roleTree &&
		Object.keys(roleTree).filter((tenant: string) => !roleTree[tenant].isAdmin);

	const hasTenantEnvironments = tenantHostnames && tenantHostnames.length > 0;
	const hasAdminEnvironments =
		adminEnvironments && adminEnvironments.length > 0;

	if (!hasTenantEnvironments && !hasAdminEnvironments) {
		return (
			<Error
				error='Your account does NOT have any roles assigned. Please ask your Admin to assign roles to your account.'
				primaryAction={() => authData.logout()}
				primaryActionText='Sign Out'
				secondaryAction={() => window.location.reload()}
				secondaryActionText='Refresh'
			/>
		);
	}
	return (
		<Stack
			direction='column'
			alignItems='center'
			sx={{ backgroundColor: 'background.default', height: '100%' }}
		>
			<LogoBlueSvg style={{ height: 180, margin: 56 }} />
			<Box component='form'>
				<Stack
					direction='column'
					alignItems='flex-start'
					spacing={2}
					sx={{
						width: 352,
						backgroundColor: 'white.lightest',
						borderRadius: 2,
						border: 1,
						borderColor: 'white.main',
						pb: 6,
						pt: 6,
						pl: 5,
						pr: 5,
					}}
				>
					{hasTenantEnvironments && hasAdminEnvironments && tab === 0 && (
						<>
							<Stack direction='row' alignItems='center' sx={{ height: 40 }}>
								<Typography variant='body1' align='left'>
									{ROLE_SELECT_SWITCH_LABEL()}
									<Link onClick={(e) => handleTabChange(e, 1)}>
										{ROLE_SELECT_SWITCH_ADMINISTRATION_LABEL()}
									</Link>
								</Typography>
							</Stack>
							<Typography variant='caption' align='left'>
								{ROLE_SELECT_WELCOME_LABEL()}
							</Typography>
						</>
					)}
					{hasTenantEnvironments && hasAdminEnvironments && tab === 1 && (
						<>
							<Stack direction='row' alignItems='center' spacing={1}>
								<IconButton
									color='primary'
									onClick={(e) => handleTabChange(e, 0)}
								>
									<ArrowBackIcon />
								</IconButton>
								<Typography
									variant='body1'
									align='left'
									sx={{ display: 'flex', alignItems: 'center' }}
								>
									{ROLE_SELECT_SWITCH_ADMINISTRATION_LABEL()}
								</Typography>
							</Stack>
							<Typography variant='caption' align='left'>
								{ROLE_SELECT_SELECT_NAMESPACE_LABEL()}
							</Typography>
						</>
					)}

					{hasTenantEnvironments && (
						<TabPanel value={tab} index={0}>
							<Stack direction='column' alignItems='center' spacing={4}>
								<TextField
									select
									fullWidth
									id='tenant-role'
									variant='outlined'
									label={ROLE_SELECT_TENANT_FIELD_LABEL()}
									size='small'
									error={selectedUserTouched && selectedUserError}
									helperText={
										selectedUserTouched && selectedUserError
											? ROLE_SELECT_FIELD_REQUIRED_MESSAGE()
											: ''
									}
									value={selectedUserRole ? selectedUserRole.role : ''}
									disabled={!roleTree}
									onBlur={() => setSelectedUserTouched(true)}
								>
									{tenantHostnames.map((environment) =>
										roleTree[environment].roles.map((role: UserRole) => (
											<MenuItem
												key={role.role}
												onClick={() =>
													handleSelect(role.role, role.tenantHostname, false)
												}
												value={role.role}
											>
												{role.tenantHostname}
											</MenuItem>
										)),
									)}
								</TextField>
								<Button
									fullWidth
									color='secondary'
									variant='contained'
									onClick={() => handleSubmit(false)}
								>
									{ROLE_SELECT_SWITCH_AND_LOGOUT_BUTTON_LABEL()}
								</Button>
							</Stack>
						</TabPanel>
					)}
					{hasAdminEnvironments && (
						<TabPanel value={tab} index={!hasTenantEnvironments ? 0 : 1}>
							<Stack direction='column' alignItems='center' spacing={4}>
								<TextField
									select
									fullWidth
									id='admin-role'
									variant='outlined'
									label={ROLE_SELECT_NAMESPACE_FIELD_LABEL()}
									size='small'
									error={selectedAdminRoleTouched && selectedAdminRoleError}
									helperText={
										selectedAdminRoleTouched && selectedAdminRoleError
											? ROLE_SELECT_FIELD_REQUIRED_MESSAGE()
											: ''
									}
									value={selectedAdminRole ? selectedAdminRole.role : ''}
									disabled={!roleTree}
									onBlur={() => setSelectedAdminRoleTouched(true)}
								>
									{adminEnvironments.map((environment) =>
										roleTree[environment].roles.map((role: UserRole) => (
											<MenuItem
												key={role.role}
												onClick={() =>
													handleSelect(role.role, role.namespace, true)
												}
												value={role.role}
											>
												{role.namespace}
											</MenuItem>
										)),
									)}
								</TextField>
								<Button
									fullWidth
									color='secondary'
									variant='contained'
									onClick={() => handleSubmit(true)}
								>
									{ROLE_SELECT_SWITCH_AND_LOGOUT_BUTTON_LABEL()}
								</Button>
							</Stack>
						</TabPanel>
					)}
				</Stack>
			</Box>
		</Stack>
	);
};
export default RoleSelect;
